import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface MediaRecord {
  id: string;
  mediaType: 'image' | 'video';
  usage: ('product-main' | 'product-secondary' | 'product-third' | 'product-extra' | 'category-hero' | 'site-static')[];
  category?: string;
  cloudinaryURL: string;
  cloudinaryPublicId?: string;
  products?: string[];
}

interface MediaContextType {
  media: MediaRecord[];
  loading: boolean;
  error: string | null;
  getMediaByUsage: (usage: string, category?: string) => MediaRecord[];
  getMediaById: (id: string) => MediaRecord | undefined;
  getMediaByProduct: (productId: string) => MediaRecord[];
  getGenericPackaging: () => string;
  refreshMedia: () => Promise<void>;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hadPreloaded = useRef(false);

  const [media, setMedia] = useState<MediaRecord[]>(() => {
    const preloaded = (window as any).__PRELOADED_DATA__?.media;
    if (!preloaded) return [];
    hadPreloaded.current = true;
    delete (window as any).__PRELOADED_DATA__.media;
    return (preloaded || [])
      .map((m: any) => ({
        id: m.id,
        mediaType: m.mediaType,
        usage: m.usage,
        category: m.category,
        cloudinaryURL: m.cloudinaryURL,
        cloudinaryPublicId: m.cloudinaryPublicId,
        products: m.products || [],
      }))
      .filter((r: MediaRecord) => r.id);
  });
  const [loading, setLoading] = useState(() => !hadPreloaded.current);
  const [error, setError] = useState<string | null>(null);

  const fetchMedia = async (bypassCache = false) => {
    try {
      const url = bypassCache ? `/api/data?type=media&_t=${Date.now()}` : '/api/data?type=media';
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch media');
      }

      const data = await response.json();

      const records: MediaRecord[] = (data.media || [])
        .map((m: any) => ({
          id: m.id,
          mediaType: m.mediaType,
          usage: m.usage,
          category: m.category,
          cloudinaryURL: m.cloudinaryURL,
          cloudinaryPublicId: m.cloudinaryPublicId,
          products: m.products || [],
        }))
        .filter((r: MediaRecord) => r.id); // Filter out records without an id

      if (import.meta.env.DEV) {
        console.debug('[media] fetched from API', {
          totalRecords: records.length,
          sampleIds: records.slice(0, 5).map(r => r.id)
        });
      }

      setMedia(records);
    } catch (err) {
      console.error('Error fetching media:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Don't block the app - just set empty media array
      setMedia([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshMedia = async () => {
    if (import.meta.env.DEV) {
      console.debug('[media] refreshing media from API');
    }
    await fetchMedia(true);
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const getMediaByUsage = (usage: string, category?: string) => {
    return media.filter(m => {
      // usage is an array from Postgres (TEXT[] field)
      const hasUsage = Array.isArray(m.usage) ? m.usage.includes(usage as any) : m.usage === usage;

      if (category) {
        return hasUsage && m.category === category;
      }
      return hasUsage;
    });
  };

  const getMediaById = (id: string) => {
    return media.find(m => m.id === id);
  };

  const getMediaByProduct = (productId: string) => {
    if (!productId) return [];

    // First try matching by Airtable record ID (for linked records in Products field)
    let matches = media.filter(m => m.products?.includes(productId));

    // If no matches, try matching by product ID/slug in media ID
    // Media IDs are like "PRODUCTNAME-main", "PRODUCTNAME-secondary", "PRODUCTNAME-VARIANT-CODE"
    if (matches.length === 0) {
      const normalizedProductId = productId.toUpperCase();

      // Try progressively shorter prefixes by removing the last dash-separated segment
      // e.g., "PESCI-COL-PLA-001" -> try "PESCI-COL-PLA-001-", then "PESCI-COL-PLA-", then "PESCI-COL-"
      const segments = normalizedProductId.split('-');

      for (let i = segments.length; i > 1; i--) {
        const prefix = segments.slice(0, i).join('-');
        matches = media.filter(m => {
          if (!m.id) return false;
          const normalizedMediaId = m.id.toUpperCase();
          // Match if media ID starts with this prefix followed by a dash
          return normalizedMediaId.startsWith(prefix + '-');
        });

        if (matches.length > 0) {
          if (import.meta.env.DEV) {
            console.debug('[media] matched with prefix', { productId, prefix, matchCount: matches.length });
          }
          break;
        }
      }
    }

    // Deduplicate by cloudinaryURL + usage combination to prevent showing truly duplicate records
    // while preserving records that have the same URL but different usages
    // (e.g., same image used for both product-main and product-secondary is valid)
    const uniqueMatches = Array.from(
      new Map(matches.map(m => {
        // Create a key that includes both URL and usage, so same URL with different usages are preserved
        const usageKey = Array.isArray(m.usage) ? m.usage.sort().join(',') : (m.usage || '');
        const key = `${m.cloudinaryURL}|${usageKey}`;
        return [key, m];
      })).values()
    );

    if (import.meta.env.DEV && uniqueMatches.length < matches.length) {
      console.debug('[media] deduplicated results', {
        before: matches.length,
        after: uniqueMatches.length,
        removed: matches.length - uniqueMatches.length
      });
    }

    return uniqueMatches;
  };

  const getGenericPackaging = (): string => {
    // Try to get from Media table first
    const packagingMedia = getMediaById('GENERIC-PACKAGING');
    if (packagingMedia?.cloudinaryURL) {
      return packagingMedia.cloudinaryURL;
    }
    // Fallback to hardcoded path
    return '/cajas.jpg';
  };

  return (
    <MediaContext.Provider
      value={{
        media,
        loading,
        error,
        getMediaByUsage,
        getMediaById,
        getMediaByProduct,
        getGenericPackaging,
        refreshMedia,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
};

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (context === undefined) {
    // Return a default context instead of throwing - more graceful
    console.warn('useMedia must be used within a MediaProvider, returning default values');
    return {
      media: [],
      loading: false,
      error: 'Not in MediaProvider',
      getMediaByUsage: () => [],
      getMediaById: () => undefined,
      getMediaByProduct: () => [],
      getGenericPackaging: () => '/cajas.jpg',
      refreshMedia: async () => {},
    };
  }
  return context;
};
